import { Button, Checkbox } from '@heroui/react';
import { ColumnDef } from '@tanstack/react-table';
import { BarChart3, MailX } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHeroToast } from 'src/hooks';

import { ContentLoadError, DataTable, Flex, ModalConfirmation, PageTitle, PageWrapper } from '../../../../components';
import { CampaignRecord, useDeleteInMailCampaigns, useGetInMailCampaigns } from '../../../../queries';
import { CampaignStatsDrawer, UnsubscribesDrawer } from '../../components';

const PAGE_SIZE = 20;

const formatDate = (value: number | null): string => (value
  ? new Date(value).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '');

/* Komórki jako osobne komponenty, a nie funkcje w `cell`: definiowane w trakcie renderu
   dostawałyby przy każdym przejściu nowy typ, więc React kasowałby i budował od nowa
   wiersze tabeli zamiast je aktualizować. */
const SubjectCell: FC<{ subject: string }> = ({ subject }) => (
  <span className="font-medium text-gray-900">{subject}</span>
);

const DeliveryCell: FC<{ campaign: CampaignRecord }> = ({ campaign }) => {
  const { sentCount, failedCount, recipientCount } = campaign;
  if (failedCount > 0) {
    return <span className="text-danger">{`${sentCount}/${recipientCount}, ${failedCount} błędów`}</span>;
  }
  return <span className="text-success">{`${sentCount}/${recipientCount}`}</span>;
};

const channelLabel = (channels: CampaignRecord['channels']): string => {
  const parts: string[] = [];
  if (channels.email) parts.push('e-mail');
  if (channels.inApp) parts.push('panel');
  return parts.join(' + ') || 'brak';
};

/**
 * Lista wysłanych wiadomości.
 *
 * Widok pokazuje wysyłki z kreatora (`inMailCampaigns`), bo to one są dziś jedyną drogą
 * wysyłki: `/inmails/new` prowadzi do kreatora, a stary formularz zszedł z trasy.
 * Statystyki otwarć siedzą w szufladzie przy wierszu, zamiast w osobnym widoku.
 */
export const InMails: FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useHeroToast();
  const { data: campaigns = [], isLoading, isError, error } = useGetInMailCampaigns();
  const { mutate: removeCampaigns, isPending: isDeletePending } = useDeleteInMailCampaigns();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statsFor, setStatsFor] = useState<CampaignRecord | null>(null);
  const [showUnsubscribes, setShowUnsubscribes] = useState(false);

  const toggleSelection = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked
      ? Array.from(new Set([...prev, id]))
      : prev.filter((item) => item !== id)));
  };

  const handleDelete = () => {
    removeCampaigns({ ids: selectedIds }, {
      onSuccess: () => {
        showSuccess('Wysyłki usunięte');
        setSelectedIds([]);
        setShowDeleteModal(false);
      },
      onError: () => showError('Nie udało się usunąć wysyłek'),
    });
  };

  const columns: ColumnDef<CampaignRecord>[] = useMemo(() => [
    {
      id: 'select',
      enableSorting: false,
      header: () => null,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <Checkbox
          isSelected={selectedIds.includes(row.original.id)}
          onValueChange={(checked) => toggleSelection(row.original.id, checked)}
        />
      ),
    },
    {
      header: 'Wiadomość',
      accessorKey: 'subject',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => <SubjectCell subject={row.original.subject} />,
    },
    {
      header: 'Kanały',
      id: 'channels',
      enableSorting: false,
      cell: ({ row }) => channelLabel(row.original.channels),
    },
    {
      header: 'Odbiorcy',
      id: 'recipients',
      cell: ({ row }) => row.original.recipientCount,
    },
    {
      header: 'Dostarczone',
      id: 'delivery',
      enableSorting: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => <DeliveryCell campaign={row.original} />,
    },
    {
      header: 'Otwarcia',
      id: 'opens',
      cell: ({ row }) => {
        const { openedCount, sentCount } = row.original;
        const rate = sentCount > 0 ? ` (${Math.round((openedCount / sentCount) * 100)}%)` : '';
        return `${openedCount}${rate}`;
      },
    },
    {
      header: 'Wysłano',
      id: 'createdAt',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      header: '',
      id: 'actions',
      enableSorting: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="light"
          startContent={<BarChart3 className="h-4 w-4" />}
          onPress={() => setStatsFor(row.original)}
        >
          Statystyki
        </Button>
      ),
    },
  ], [selectedIds]);

  return (
    <PageWrapper>
      {isError ? (
        /* Najczęstszy powód to brak wdrożonych reguł Firestore dla `inMailCampaigns`:
           odczyt wraca wtedy jako `permission-denied` i bez tej podpowiedzi wygląda jak
           awaria panelu. */
        <ContentLoadError
          message={error?.code === 'permission-denied'
            ? 'Brak dostępu do listy wysyłek. Wdróż reguły Firestore (firebase deploy --only firestore:rules).'
            : error?.message}
        />
      ) : (
        <>
          <PageTitle
            title="InMails"
            actions={(
              <Flex gap="15px">
                {selectedIds.length > 0 && (
                  <Button size="sm" color="danger" radius="sm" onPress={() => setShowDeleteModal(true)}>
                    {`Usuń zaznaczone (${selectedIds.length})`}
                  </Button>
                )}
                <Button
                  className="self-end"
                  size="sm"
                  variant="bordered"
                  radius="sm"
                  startContent={<MailX className="h-4 w-4" />}
                  onPress={() => setShowUnsubscribes(true)}
                >
                  Wypisani
                </Button>
                <Button
                  className="self-end"
                  size="sm"
                  color="secondary"
                  radius="sm"
                  onPress={() => navigate('/inmails/new')}
                >
                  Nowa wiadomość
                </Button>
              </Flex>
            )}
          />

          <DataTable
            data={campaigns}
            columns={columns}
            isLoading={isLoading}
            isError={isError}
            pageSize={PAGE_SIZE}
            searchPlaceholder="Szukaj po temacie..."
          />

          {statsFor && <CampaignStatsDrawer campaign={statsFor} onClose={() => setStatsFor(null)} />}
          {showUnsubscribes && <UnsubscribesDrawer onClose={() => setShowUnsubscribes(false)} />}

          <ModalConfirmation
            needConfirm={false}
            confirmValue="inmails"
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDelete}
            title="Usuń wysyłki"
            description={`Czy na pewno usunąć ${selectedIds.length} wysyłek? Wiadomości, które trafiły już do skrzynek, zostaną nietknięte.`}
            isLoading={isDeletePending}
          />
        </>
      )}
    </PageWrapper>
  );
};
